// 공간 지각 — 머릿속으로 돌리고, 맞추고, 접는다.
//  · 회전: 돌려서(뒤집지 않고) 같은 모양 고르기 — 오답에 뒤집힌 모양이 섞인다
//  · 조각 맞추기: 주어진 모양을 빈틈없이 채우는 조각 묶음 — 오답 묶음은 채울 수 없음을 풀이기로 확인한다
//  · 종이 접기: 접고 구멍을 뚫은 뒤 펼친 모양
//  · 전개도: 접었을 때 마주 보는 면 — 실제로 3차원에서 접어 계산한다
import type { ChoiceQuestion } from './ChoiceDrill';
import { int, makeRng, pick, shuffle, type Rng } from './rng';

export type Cell = [number, number];
export type Fold = 'L' | 'R' | 'T' | 'B';
export interface PaperFrame { region: [number, number, number, number]; fold?: Fold; axis?: number }

export type SpaceQuestion = ChoiceQuestion & { ask: string } & (
  | { kind: 'rotate'; shape: Cell[]; options: Cell[][] }
  | { kind: 'pieces'; shape: Cell[]; options: Cell[][][] }
  | { kind: 'paper'; frames: PaperFrame[]; holes: Cell[]; options: Cell[][] }
  | { kind: 'net'; net: { cell: Cell; sym: string }[]; target: string; options: string[] }
);

export const SPACE_COUNT = 6;

// ---- 칸 모양(폴리오미노) ----
export function norm(cells: Cell[]): Cell[] {
  const mx = Math.min(...cells.map((c) => c[0])), my = Math.min(...cells.map((c) => c[1]));
  return cells.map(([x, y]) => [x - mx, y - my] as Cell).sort((a, b) => a[1] - b[1] || a[0] - b[0]);
}
export const shapeKey = (cells: Cell[]) => norm(cells).map((c) => c.join(',')).join(';');
export const rot90 = (cells: Cell[]): Cell[] => norm(cells.map(([x, y]) => [-y, x] as Cell));
export const mirror = (cells: Cell[]): Cell[] => norm(cells.map(([x, y]) => [-x, y] as Cell));
export function rotations(cells: Cell[]): Cell[][] {
  const out = [norm(cells)];
  for (let i = 1; i < 4; i++) out.push(rot90(out[i - 1]));
  return out;
}
/** 돌려도 같으면 같은 조각으로 보는 이름 */
export const pieceId = (cells: Cell[]) => rotations(cells).map(shapeKey).sort()[0];
const NB: Cell[] = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const has = (cells: Cell[], [x, y]: Cell) => cells.some((c) => c[0] === x && c[1] === y);

export function connected(cells: Cell[]): boolean {
  if (!cells.length) return false;
  const seen = new Set([cells[0].join(',')]);
  const stack = [cells[0]];
  while (stack.length) {
    const [x, y] = stack.pop()!;
    for (const [dx, dy] of NB) {
      const k = `${x + dx},${y + dy}`;
      if (!seen.has(k) && has(cells, [x + dx, y + dy])) {
        seen.add(k);
        stack.push([x + dx, y + dy]);
      }
    }
  }
  return seen.size === cells.length;
}

/** 무작위 칸 모양 — compact 가 클수록 둥근(이웃이 많은) 칸을 더 붙인다 */
function randomPoly(r: Rng, k: number, compact = 0): Cell[] {
  const cells: Cell[] = [[0, 0]];
  while (cells.length < k) {
    const frontier: Cell[] = [];
    for (const [x, y] of cells) for (const [dx, dy] of NB) {
      const c: Cell = [x + dx, y + dy];
      if (!has(cells, c) && !has(frontier, c)) frontier.push(c);
    }
    const weight = (c: Cell) => 1 + compact * NB.filter(([dx, dy]) => has(cells, [c[0] + dx, c[1] + dy])).length;
    const total = frontier.reduce((s, c) => s + weight(c), 0);
    let t = r() * total;
    const chosen = frontier.find((c) => (t -= weight(c)) < 0) ?? frontier[frontier.length - 1];
    cells.push(chosen);
  }
  return norm(cells);
}

/** 칸 하나를 다른 자리로 옮긴 모양 (연결 유지) */
function nudge(r: Rng, cells: Cell[]): Cell[] | null {
  for (let t = 0; t < 30; t++) {
    const i = int(r, 0, cells.length - 1);
    const rest = cells.filter((_, j) => j !== i);
    if (!connected(rest)) continue;
    const frontier: Cell[] = [];
    for (const [x, y] of rest) for (const [dx, dy] of NB) {
      const c: Cell = [x + dx, y + dy];
      if (!has(rest, c) && !(c[0] === cells[i][0] && c[1] === cells[i][1]) && !has(frontier, c)) frontier.push(c);
    }
    if (frontier.length) return norm([...rest, pick(r, frontier)]);
  }
  return null;
}

// ---- 회전 ----
function rotateQ(r: Rng, lv: number): SpaceQuestion | null {
  const k = lv <= 2 ? 4 : lv <= 4 ? 5 : lv <= 7 ? 6 : 7;
  const shape = randomPoly(r, k);
  const rots = rotations(shape).map(shapeKey);
  const mirrors = rotations(mirror(shape));
  if (new Set(rots).size < 4 || mirrors.some((m) => rots.includes(shapeKey(m)))) return null; // 대칭이면 뒤집기 함정이 안 된다
  const answer = rotations(shape)[int(r, 1, 3)];
  const wrong: Cell[][] = shuffle(r, mirrors).slice(0, 2);
  for (let t = 0; t < 20 && wrong.length < 3; t++) {
    const other = nudge(r, shape);
    if (!other) continue;
    const ok = !rotations(other).some((o) => rots.includes(shapeKey(o)) || mirrors.some((m) => shapeKey(m) === shapeKey(o)));
    if (ok && !wrong.some((w) => pieceId(w) === pieceId(other))) wrong.push(rotations(other)[int(r, 0, 3)]);
  }
  if (wrong.length < 3) return null;
  const options = shuffle(r, [answer, ...wrong]);
  return {
    kind: 'rotate', shape, options, answer: options.indexOf(answer), n: 4, limitSec: 30,
    ask: '위 모양을 돌려서 만들 수 있는 것은? (뒤집으면 안 됨)',
    explain: '정답만 돌린 모양입니다. 나머지는 뒤집혔거나(거울에 비친 모양) 칸 하나가 다릅니다.',
  };
}

// ---- 조각 맞추기 ----
/** 모양을 조각들로 빈틈없이 채울 수 있나 (돌리기만, 뒤집기 없음) */
export function canTile(target: Cell[], pieces: Cell[][]): boolean {
  if (pieces.reduce((s, p) => s + p.length, 0) !== target.length) return false;
  const orients = pieces.map((p) => [...new Map(rotations(p).map((o) => [shapeKey(o), o])).values()]);
  const left = new Set(target.map((c) => c.join(',')));
  const used = pieces.map(() => false);
  const solve = (): boolean => {
    if (!left.size) return true;
    const first = [...left].map((s) => s.split(',').map(Number) as Cell).sort((a, b) => a[1] - b[1] || a[0] - b[0])[0];
    for (let i = 0; i < pieces.length; i++) {
      if (used[i]) continue;
      for (const o of orients[i]) {
        const [ax, ay] = o[0];
        const placed = o.map(([x, y]) => `${x - ax + first[0]},${y - ay + first[1]}`);
        if (!placed.every((c) => left.has(c))) continue;
        placed.forEach((c) => left.delete(c));
        used[i] = true;
        if (solve()) return true;
        used[i] = false;
        placed.forEach((c) => left.add(c));
      }
    }
    return false;
  };
  return solve();
}

function partition(r: Rng, target: Cell[], m: number): Cell[][] | null {
  const owner = new Map<string, number>();
  const seeds = shuffle(r, target).slice(0, m);
  seeds.forEach((c, i) => owner.set(c.join(','), i));
  for (let guard = 0; owner.size < target.length && guard < 500; guard++) {
    const i = int(r, 0, m - 1);
    const mine = target.filter((c) => owner.get(c.join(',')) === i);
    const grow = target.filter((c) => !owner.has(c.join(',')) && mine.some((o) => Math.abs(o[0] - c[0]) + Math.abs(o[1] - c[1]) === 1));
    if (grow.length) owner.set(pick(r, grow).join(','), i);
  }
  if (owner.size < target.length) return null;
  const pieces = Array.from({ length: m }, (_, i) => target.filter((c) => owner.get(c.join(',')) === i));
  return pieces.every((p) => p.length >= 2 && connected(p)) ? pieces.map(norm) : null;
}

const setId = (pieces: Cell[][]) => pieces.map(pieceId).sort().join('|');

function piecesQ(r: Rng, lv: number): SpaceQuestion | null {
  const m = lv <= 6 ? 2 : 3;
  const target = randomPoly(r, m === 2 ? int(r, 6, 8) : int(r, 8, 10), 2);
  const pieces = partition(r, target, m);
  if (!pieces) return null;
  const wrong: Cell[][][] = [];
  const ids = new Set([setId(pieces)]);
  for (let t = 0; t < 60 && wrong.length < 3; t++) {
    const i = int(r, 0, m - 1);
    const changed = nudge(r, pieces[i]);
    if (!changed) continue;
    const alt = pieces.map((p, j) => (j === i ? changed : p));
    const id = setId(alt);
    if (ids.has(id) || canTile(target, alt)) continue;
    ids.add(id);
    wrong.push(alt);
  }
  if (wrong.length < 3) return null;
  const show = (set: Cell[][]) => shuffle(r, set.map((p) => rotations(p)[int(r, 0, 3)]));
  const right = show(pieces);
  const options = shuffle(r, [right, ...wrong.map(show)]);
  return {
    kind: 'pieces', shape: target, options, answer: options.indexOf(right), n: 4, limitSec: 70,
    ask: '위 모양을 빈틈없이 채울 수 있는 조각 묶음은? (조각은 돌릴 수 있지만 뒤집을 수는 없음)',
    explain: '정답 묶음만 돌려서 끼우면 딱 맞습니다. 나머지 묶음은 칸 수는 같지만 어떻게 돌려도 빈틈이나 겹침이 생깁니다.',
  };
}

// ---- 종이 접기 ----
const GRID = 4;
export const holeKey = (cells: Cell[]) => cells.map((c) => c.join(',')).sort().join(';');
/** 접힌 선(axis)을 기준으로 펼칠 때 구멍이 옮겨 가는 자리 */
const reflect = (c: Cell, frame: PaperFrame): Cell => (frame.fold === 'L' || frame.fold === 'R' ? [2 * frame.axis! - 1 - c[0], c[1]] : [c[0], 2 * frame.axis! - 1 - c[1]]);

export function unfold(holes: Cell[], frames: PaperFrame[], skipLast = 0, swapAxis = -1): Cell[] {
  let cur = holes.map((c) => [...c] as Cell);
  const folds = frames.filter((f) => f.fold);
  for (let i = folds.length - 1; i >= skipLast; i--) {
    let f = folds[i];
    if (i === swapAxis) f = { ...f, fold: f.fold === 'L' || f.fold === 'R' ? 'T' : 'L', axis: GRID / 2 };
    const add = cur.map((c) => reflect(c, f));
    cur = [...new Map([...cur, ...add].map((c) => [c.join(','), c])).values()];
  }
  return cur.filter(([x, y]) => x >= 0 && y >= 0 && x < GRID && y < GRID);
}

function paperQ(r: Rng, lv: number): SpaceQuestion | null {
  const nFolds = lv <= 4 ? 1 : 2;
  let [x0, y0, x1, y1] = [0, 0, GRID, GRID];
  const frames: PaperFrame[] = [];
  for (let k = 0; k < nFolds; k++) {
    const dir = pick(r, ['L', 'R', 'T', 'B'] as Fold[]);
    const vertical = dir === 'L' || dir === 'R';
    const axis = vertical ? (x0 + x1) / 2 : (y0 + y1) / 2;
    if ((vertical ? x1 - x0 : y1 - y0) < 2) return null;
    frames.push({ region: [x0, y0, x1, y1], fold: dir, axis });
    if (dir === 'L') x0 = axis;
    else if (dir === 'R') x1 = axis;
    else if (dir === 'T') y0 = axis;
    else y1 = axis;
  }
  frames.push({ region: [x0, y0, x1, y1] });
  const spots: Cell[] = [];
  for (let x = x0; x < x1; x++) for (let y = y0; y < y1; y++) spots.push([x, y]);
  const holes = shuffle(r, spots).slice(0, lv >= 8 ? 2 : 1);
  const answer = unfold(holes, frames);
  const cands: Cell[][] = [
    unfold(holes, frames, 1),                                             // 마지막 접기(처음 접은 것)를 안 펼침
    unfold(holes, frames, 0, 0),                                          // 첫 접기 선을 잘못 봄
    unfold(holes, frames, 0, frames.filter((f) => f.fold).length - 1),    // 둘째 접기 선을 잘못 봄
    answer.map(([x, y]) => [Math.min(GRID - 1, x + 1), y] as Cell),       // 한 칸 밀림
    answer.map(([x, y]) => [x, (y + 1) % GRID] as Cell),
    answer.slice(1),                                                      // 구멍 하나 빠짐
  ];
  const seen = new Set([holeKey(answer)]);
  const wrong: Cell[][] = [];
  for (const c of shuffle(r, cands)) {
    const k = holeKey(c);
    if (c.length && !seen.has(k)) {
      seen.add(k);
      wrong.push(c);
    }
    if (wrong.length >= 3) break;
  }
  if (wrong.length < 3) return null;
  const options = shuffle(r, [answer, ...wrong]);
  return {
    kind: 'paper', frames, holes, options, answer: options.findIndex((o) => holeKey(o) === holeKey(answer)), n: 4, limitSec: 45,
    ask: '종이를 차례로 접고 구멍을 뚫은 뒤 다 펼치면?',
    explain: '접은 순서의 반대로 하나씩 펼치면, 구멍은 접힌 선을 기준으로 거울처럼 하나씩 늘어납니다.',
  };
}

// ---- 전개도 ----
export const NETS = ['1000/1111/1000', '1000/1111/0100', '1000/1111/0010', '1000/1111/0001', '0100/1111/0100', '0100/1111/0010',
  '1100/0111/0100', '1100/0111/0010', '1100/0111/0001', '1100/0110/0011', '11100/00111'];
export const netCells = (s: string): Cell[] => s.split('/').flatMap((row, y) => [...row].flatMap((ch, x) => (ch === '1' ? [[x, y] as Cell] : [])));
type V = [number, number, number];
const neg = (v: V): V => [-v[0], -v[1], -v[2]];

/** 전개도를 실제로 접어 칸마다 바깥 방향(법선)을 구한다. 바닥 칸에서 오른쪽으로 넘어가면 그 칸이 일어나 오른쪽 면이 되는 식 */
export function foldNet(cells: Cell[]): Map<string, V> {
  const out = new Map<string, V>();
  const start = cells[0];
  const queue: [Cell, V, V, V][] = [[start, [0, 0, -1], [1, 0, 0], [0, 1, 0]]]; // [칸, 법선, 오른쪽, 아래쪽]
  out.set(start.join(','), [0, 0, -1]);
  while (queue.length) {
    const [[x, y], N, R, D] = queue.shift()!;
    const moves: [Cell, V, V, V][] = [
      [[x + 1, y], R, neg(N), D],
      [[x - 1, y], neg(R), N, D],
      [[x, y + 1], D, R, neg(N)],
      [[x, y - 1], neg(D), R, N],
    ];
    for (const [c, n2, r2, d2] of moves) {
      if (!has(cells, c) || out.has(c.join(','))) continue;
      out.set(c.join(','), n2);
      queue.push([c, n2, r2, d2]);
    }
  }
  return out;
}

const SYMBOLS = ['★', '●', '▲', '■', '◆', '♥'];

function netQ(r: Rng, lv: number): SpaceQuestion {
  let cells = netCells(lv <= 2 ? pick(r, NETS.slice(0, 6)) : pick(r, NETS));
  for (let i = int(r, 0, 3); i > 0; i--) cells = rot90(cells);
  if (r() < 0.5) cells = mirror(cells);
  const normals = foldNet(cells);
  const syms = shuffle(r, SYMBOLS);
  const net = cells.map((cell, i) => ({ cell, sym: syms[i] }));
  const t = int(r, 0, 5);
  const nT = normals.get(cells[t].join(','))!;
  const opp = net.find((f) => normals.get(f.cell.join(','))!.every((v, k) => v === -nT[k]))!.sym;
  const others = shuffle(r, net.filter((f, i) => i !== t && f.sym !== opp).map((f) => f.sym)).slice(0, 3);
  const options = shuffle(r, [opp, ...others]);
  return {
    kind: 'net', net, target: net[t].sym, options, answer: options.indexOf(opp), n: 4, limitSec: 35,
    ask: `전개도를 접어 정육면체를 만들 때 ${net[t].sym} 와 마주 보는 면은?`,
    explain: `요령: 한 줄로 이어진 세 칸에서 양 끝은 마주 봅니다. 꺾인 곳은 접히는 모서리를 따라 한 칸씩 굴려 보세요. ${net[t].sym} ↔ ${opp}`,
  };
}

export function makeSpaceRound(level: number, seed: number): SpaceQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const plan: ('rotate' | 'net' | 'paper' | 'pieces')[] =
    lv <= 2 ? ['rotate', 'rotate', 'rotate', 'net', 'net', 'net']
      : lv <= 4 ? ['rotate', 'rotate', 'net', 'net', 'paper', 'paper']
        : lv <= 6 ? ['rotate', 'rotate', 'net', 'paper', 'pieces', 'pieces']
          : ['rotate', 'net', 'paper', 'paper', 'pieces', 'pieces'];
  const out: SpaceQuestion[] = [];
  for (const kind of shuffle(rng, plan)) {
    for (let guard = 0; guard < 200; guard++) {
      const q = kind === 'rotate' ? rotateQ(rng, lv) : kind === 'pieces' ? piecesQ(rng, lv) : kind === 'paper' ? paperQ(rng, lv) : netQ(rng, lv);
      if (q) {
        out.push(q);
        break;
      }
    }
  }
  return out;
}
